import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import useEscapeToClose from '../hooks/useEscapeToClose';
import CardArt from './CardArt';
import colors from '../theme/colors';
import NodeCardPreview from './NodeCardPreview';
import { IconBulb, IconInfo, IconEye } from './icons';

// ── ZzzZZZ sleep icon ────────────────────────────────────────────────────────
const ZzzIcon = () => (
  <svg viewBox="0 0 50 50" fill="none" xmlns="http://www.w3.org/2000/svg" className="chain-endcap" style={{ flexShrink: 0 }}>
    <text x="2" y="22" fontSize="12" fill={colors.base.accentPrimary} opacity="0.6" fontWeight="bold">z</text>
    <text x="10" y="32" fontSize="16" fill={colors.base.accentPrimary} opacity="0.8" fontWeight="bold">z</text>
    <text x="22" y="42" fontSize="20" fill={colors.base.accentPrimary} fontWeight="bold">Z</text>
  </svg>
);

// ── Rising sun icon — start of the day, before the first idea ────────────────
const SunIcon = () => (
  <svg viewBox="0 0 50 50" fill="none" xmlns="http://www.w3.org/2000/svg" className="chain-endcap" style={{ flexShrink: 0 }}>
    <circle cx="25" cy="27" r="8" stroke={colors.base.accentPrimary} strokeWidth="2.5" opacity="0.9" />
    <g stroke={colors.base.accentPrimary} strokeWidth="2.5" strokeLinecap="round" opacity="0.7">
      <path d="M25 10v5M25 39v5M8 27h5M37 27h5M13 15l3.5 3.5M33.5 38.5 37 42M37 15l-3.5 3.5M16.5 38.5 13 42" />
    </g>
  </svg>
);

// ── Styled components ────────────────────────────────────────────────────────
const ButtonContainer = styled.div`
  position: fixed;
  /* Default position — same column as MusicPlayer, one slot further left */
  top: 30px;
  right: 118px;
  z-index: 15000;
  display: flex;
  flex-direction: column;
  align-items: center;

  /* Mobile: right under the music button, which sits top-left (10px, 10px).
     left 6px (not 10) compensates for the label being wider than the icon,
     so the bulb centers on the same column as the music icon */
  @media (max-width: 768px) {
    top: 58px;
    left: 6px;
    right: auto;
  }
`;

const TriggerButton = styled.button`
  background: none;
  border: none;
  color: ${colors.accent.primary};
  width: 30px;
  height: 30px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  padding: 0;
  outline: none;

  &:hover { opacity: 0.8; transform: scale(1.1); }
  &:active { opacity: 0.6; transform: scale(0.95); }
  &:focus { outline: none; }
`;

const ButtonLabel = styled.div`
  margin-top: 2px;
  color: ${colors.accent.primary};
  font-size: 8px;
  font-weight: 600;
  text-shadow: 0 1px 5px rgba(45, 27, 61, 0.6);
  line-height: 1;
  text-align: center;

  /* Mobile: wrap onto two lines so the label stays as narrow as the icon
     and the bulb lines up with the music button above it */
  @media (max-width: 768px) {
    max-width: 38px;
    white-space: normal;
    line-height: 1.15;
  }
`;

const Popup = styled(motion.div)`
  position: fixed;
  /* Same treatment as the other modals: translucent theme blue + white streaks */
  background-color: ${colors.base.themePrimaryTransparent};
  background-image:
    linear-gradient(73deg,  transparent calc(47% - 1px), rgba(255,255,255,0.14) 47%, transparent calc(47% + 1px)),
    linear-gradient(161deg, transparent calc(38% - 1px), rgba(255,255,255,0.10) 38%, transparent calc(38% + 1px)),
    linear-gradient(210deg, transparent calc(55% - 1px), rgba(255,255,255,0.08) 55%, transparent calc(55% + 1px)),
    linear-gradient(30deg,  transparent calc(68% - 1px), rgba(255,255,255,0.06) 68%, transparent calc(68% + 1px));
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 24px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
  padding: 20px 24px 24px;
  z-index: 15001;
  min-width: 340px;
  max-width: min(92vw, 500px);

  @media (max-width: 768px) {
    min-width: 0;
    width: min(92vw, 440px);
    padding: 16px;
  }
`;

const PopupHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
`;

const PopupTitle = styled.h3`
  color: ${colors.accent.primary};
  font-size: 1rem;
  font-weight: 700;
  margin: 0;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const CloseBtn = styled.button`
  background: none;
  border: none;
  color: rgba(255,255,255,0.6);
  font-size: 1.1rem;
  cursor: pointer;
  padding: 0 2px;
  transition: color 0.2s;
  &:hover { color: white; }
`;

const InfoBtn = styled.button`
  background: none;
  border: 1px solid rgba(255,255,255,0.3);
  border-radius: 50%;
  width: 22px;
  height: 22px;
  color: rgba(255,255,255,0.6);
  font-size: 0.7rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  &:hover { color: white; border-color: white; }
`;

const InfoTooltip = styled(motion.div)`
  background: rgba(0,0,0,0.6);
  border: 1px solid rgba(255,255,255,0.15);
  border-radius: 12px;
  padding: 10px 14px;
  margin-bottom: 14px;
  color: rgba(255, 255, 255,0.85);
  font-size: 0.78rem;
  line-height: 1.5;
  overflow: hidden;
`;

const ChainRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 0;
  overflow-x: auto;
  padding: 4px 2px 8px;
  -webkit-overflow-scrolling: touch;

  .chain-endcap { width: 44px; height: 44px; }

  /* Mobile: compact chain (same scale as the community modal) and no visible
     scrollbar — it steals row height and looks broken */
  @media (max-width: 768px) {
    .chain-endcap { width: 32px; height: 32px; }
    scrollbar-width: none;
    &::-webkit-scrollbar { display: none; }
  }
`;

const ConnectorLine = styled.div`
  width: 14px;
  height: 2px;
  background: rgba(255,255,255,0.2);
  flex-shrink: 0;

  @media (max-width: 768px) {
    width: 10px;
  }
`;

const CircleWrapper = styled.div`
  position: relative;
  flex-shrink: 0;
`;

const SolutionCircle = styled.div`
  width: 54px;
  height: 54px;

  /* Mobile: same circle size as the community modal, avoids horizontal scroll */
  @media (max-width: 768px) {
    width: 42px;
    height: 42px;
  }

  border-radius: 50%;
  border: 2px solid ${props => props.$filled ? colors.base.accentPrimary : 'rgba(255,255,255,0.2)'};
  background: ${props => props.$filled ? 'rgba(255, 255, 255,0.1)' : 'rgba(255,255,255,0.05)'};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: ${props => props.$filled ? 'grab' : 'default'};
  transition: all 0.2s ease;
  overflow: hidden;
  user-select: none;

  &:hover {
    border-color: ${props => props.$filled ? colors.accent.primaryHighlight : 'rgba(255,255,255,0.35)'};
    background: ${props => props.$filled ? 'rgba(255, 255, 255,0.18)' : 'rgba(255,255,255,0.08)'};
  }

  &[data-dragging="true"] { opacity: 0.45; cursor: grabbing; }

  &[data-dragover="true"] {
    border-color: ${colors.accent.primary};
    background: rgba(255, 255, 255,0.22);
    transform: scale(1.08);
  }
`;

const CircleNumber = styled.span`
  color: rgba(255,255,255,0.25);
  font-size: 1rem;
  font-weight: 600;
`;

const PeekBtn = styled.button`
  display: none; /* desktop has hover previews */
  position: absolute;
  bottom: -7px;
  right: -7px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 1px solid rgba(255,255,255,0.25);
  background: rgba(0,0,0,0.6);
  color: white;
  line-height: 1;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  z-index: 2;

  @media (hover: none), (max-width: 768px) {
    display: flex;
  }
`;

const EmptyHint = styled.div`
  text-align: center;
  color: rgba(255, 255, 255,0.35);
  font-size: 0.78rem;
  margin-top: 4px;
`;

// ── Component ────────────────────────────────────────────────────────────────
const MySolutionsPopup = ({ allNodes = [], onNavigateToNode }) => {
  const { t, i18n } = useTranslation();
  const { isAuthenticated, solutions, reorderSolutions } = useAuth();
  const [open, setOpen] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const [hoveredIdx, setHoveredIdx] = useState(null);
  const [hoveredNodeData, setHoveredNodeData] = useState(null);
  const [hoveredRect, setHoveredRect] = useState(null);
  const [peeked, setPeeked] = useState(false); // preview opened via eye tap (mobile)
  const [dragIdx, setDragIdx] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);
  const [droppingOutside, setDroppingOutside] = useState(false);
  const [popupPos, setPopupPos] = useState({ top: 70, right: 80 });
  const buttonRef = useRef(null);

  const isFr = i18n.language?.startsWith('fr');
  const pickLang = (en, fr) => (isFr ? (fr || en) : (en || fr)) || '';

  // Dynamic positioning — tries to align with MusicPlayer row, falls back to CSS default
  useEffect(() => {
    const updatePosition = () => {
      if (!buttonRef.current) return;

      // Mobile: keep the CSS slot under the music button — clear any inline
      // override so a resize across the breakpoint doesn't leave stale values
      if (window.innerWidth <= 768) {
        buttonRef.current.style.top = '';
        buttonRef.current.style.right = '';
        buttonRef.current.style.left = '';
        return;
      }

      const profile = document.getElementById('user-profile');
      const loginContainer = document.getElementById('auth-login-container');
      const target = profile || loginContainer;
      if (!target) return;

      const rect = target.getBoundingClientRect();
      const buttonSize = 30;
      const gap = 10;
      const offsetFromProfile = 92 + gap + buttonSize; // same offset math as MusicPlayer
      const top = Math.round(rect.top + (rect.height - buttonSize) / 2) - 4;
      const right = Math.round(window.innerWidth - rect.left + offsetFromProfile - buttonSize);

      // Apply via inline style directly (keep CSS default as fallback)
      if (buttonRef.current) {
        buttonRef.current.style.top = `${top}px`;
        buttonRef.current.style.right = `${right}px`;
        buttonRef.current.style.left = 'auto';
      }
    };

    updatePosition();
    const interval = setInterval(updatePosition, 500);
    window.addEventListener('resize', updatePosition);
    return () => { clearInterval(interval); window.removeEventListener('resize', updatePosition); };
  }, []);

  // Update popup position when popup opens
  useEffect(() => {
    if (!open || !buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    // Anchor below the button, clamped so the popup never overflows either edge
    const popupWidth = Math.min(window.innerWidth * 0.92, 500);
    let right = window.innerWidth - rect.right - 8;
    right = Math.max(8, Math.min(right, window.innerWidth - popupWidth - 8));
    setPopupPos({ top: rect.bottom + 8, right });
  }, [open]);

  const sorted = [...solutions].sort((a, b) => a.position - b.position);

  const clearHover = () => { setHoveredIdx(null); setHoveredNodeData(null); setHoveredRect(null); setPeeked(false); };

  useEscapeToClose(open, () => { clearHover(); setOpen(false); });

  // Tap-to-preview (eye): close when tapping anywhere outside an eye button
  useEffect(() => {
    if (!peeked || !hoveredNodeData) return;
    const onDocPointerDown = (e) => {
      if (e.target.closest && e.target.closest('[data-peek]')) return;
      clearHover();
    };
    document.addEventListener('pointerdown', onDocPointerDown);
    return () => document.removeEventListener('pointerdown', onDocPointerDown);
  }, [peeked, hoveredNodeData]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCircleClick = (sol) => {
    if (!sol || !onNavigateToNode) return;
    clearHover();
    onNavigateToNode(sol.node_id);
    setOpen(false);
  };

  const handleDragStart = (idx) => setDragIdx(idx);
  const handleDragOver = (e, idx) => { e.preventDefault(); setDragOverIdx(idx); };
  const handleDrop = (e, idx) => {
    e.preventDefault();
    if (dragIdx === null || dragIdx === idx) { setDragIdx(null); setDragOverIdx(null); return; }
    const newList = [...sorted];
    const [moved] = newList.splice(dragIdx, 1);
    newList.splice(idx, 0, moved);
    reorderSolutions(newList.map((s, i) => ({ ...s, position: i + 1 })));
    setDragIdx(null);
    setDragOverIdx(null);
  };
  const handleDragEnd = () => { setDragIdx(null); setDragOverIdx(null); setDroppingOutside(false); };

  const handleBackdropDragOver = (e) => {
    e.preventDefault();
    if (dragIdx !== null) setDroppingOutside(true);
  };
  const handleBackdropDragLeave = () => setDroppingOutside(false);
  const handleBackdropDrop = (e) => {
    e.preventDefault();
    if (dragIdx !== null) {
      const newList = sorted.filter((_, i) => i !== dragIdx).map((s, i) => ({ ...s, position: i + 1 }));
      reorderSolutions(newList);
    }
    setDroppingOutside(false);
    setDragIdx(null);
    setDragOverIdx(null);
  };

  if (!isAuthenticated) return null;

  return (
    <>
      <ButtonContainer ref={buttonRef}>
        <TriggerButton onClick={() => { clearHover(); setOpen(v => !v); }} title={t('solutions.mySolutions')}>
          <IconBulb size={24} />
        </TriggerButton>
        <ButtonLabel>{t('solutions.mySolutionsShort')}</ButtonLabel>
      </ButtonContainer>

      {createPortal(
        <AnimatePresence>
          {open && (
            <>
              <div
                style={{
                  position: 'fixed', inset: 0, zIndex: 15000,
                  background: droppingOutside ? 'rgba(220,50,50,0.12)' : 'transparent',
                  transition: 'background 0.15s',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
                onClick={() => { clearHover(); setOpen(false); }}
                onDragOver={handleBackdropDragOver}
                onDragLeave={handleBackdropDragLeave}
                onDrop={handleBackdropDrop}
              >
                {droppingOutside && (
                  <div style={{
                    color: 'rgba(255,100,100,0.85)', fontSize: '2rem',
                    background: 'rgba(0,0,0,0.55)', borderRadius: '50%',
                    width: 64, height: 64, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    pointerEvents: 'none',
                  }}>🗑</div>
                )}
              </div>
              <Popup
                style={{ top: popupPos.top, right: popupPos.right }}
                initial={{ opacity: 0, y: -8, scale: 0.96 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -8, scale: 0.96 }}
                transition={{ duration: 0.18 }}
              >
                <PopupHeader>
                  <PopupTitle>{t('solutions.mySolutions')}</PopupTitle>
                  <HeaderActions>
                    <InfoBtn onClick={() => setShowInfo(v => !v)}><IconInfo size={12} /></InfoBtn>
                    <CloseBtn onClick={() => { clearHover(); setOpen(false); }}>✕</CloseBtn>
                  </HeaderActions>
                </PopupHeader>

                <AnimatePresence>
                  {showInfo && (
                    <InfoTooltip
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                    >
                      {t('solutions.mySolutionsInfo')}
                    </InfoTooltip>
                  )}
                </AnimatePresence>

                <ChainRow>
                  <SunIcon />
                  <ConnectorLine />
                  {Array.from({ length: 5 }).map((_, i) => {
                    const sol = sorted[i] || null;
                    const isFilled = !!sol;
                    const solTitle = sol ? pickLang(sol.title_en, sol.title_fr) : '';
                    const solDesc  = sol ? pickLang(sol.short_description_en, sol.short_description_fr) : '';

                    return (
                      <React.Fragment key={i}>
                        {i > 0 && <ConnectorLine />}
                        <CircleWrapper>
                          <SolutionCircle
                            $filled={isFilled}
                            data-dragging={dragIdx === i ? 'true' : 'false'}
                            data-dragover={dragOverIdx === i ? 'true' : 'false'}
                            draggable={isFilled}
                            onDragStart={() => handleDragStart(i)}
                            onDragOver={(e) => handleDragOver(e, i)}
                            onDrop={(e) => handleDrop(e, i)}
                            onDragEnd={handleDragEnd}
                            onClick={() => isFilled && handleCircleClick(sol)}
                            onMouseEnter={(e) => {
                              if (!isFilled) return;
                              setHoveredIdx(i);
                              setHoveredRect(e.currentTarget.getBoundingClientRect());
                              setHoveredNodeData({
                                id: sol.node_id,
                                title: solTitle,
                                colorCode: sol.color_code,
                                shortDescription: solDesc,
                              });
                            }}
                            onMouseLeave={() => { setHoveredIdx(null); setHoveredNodeData(null); setHoveredRect(null); }}
                          >
                            {isFilled ? (
                              <CardArt id={sol.node_id} title={solTitle} />
                            ) : (
                              <CircleNumber>{i + 1}</CircleNumber>
                            )}
                          </SolutionCircle>
                          {isFilled && (
                            <PeekBtn
                              data-peek
                              title={solTitle}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (hoveredIdx === i) {
                                  clearHover();
                                  return;
                                }
                                setPeeked(true);
                                setHoveredIdx(i);
                                setHoveredRect(e.currentTarget.parentElement.getBoundingClientRect());
                                setHoveredNodeData({
                                  id: sol.node_id,
                                  title: solTitle,
                                  colorCode: sol.color_code,
                                  shortDescription: solDesc,
                                });
                              }}
                            >
                              <IconEye size={12} />
                            </PeekBtn>
                          )}
                          {/* card preview is rendered via portal in NodeCardPreview */}
                        </CircleWrapper>
                      </React.Fragment>
                    );
                  })}
                  <ConnectorLine />
                  <ZzzIcon />
                </ChainRow>

                {sorted.length === 0 && (
                  <EmptyHint>{t('solutions.noSolutionsYet')}</EmptyHint>
                )}
              </Popup>
            </>
          )}
        </AnimatePresence>,
        document.body
      )}

      <AnimatePresence>
        {hoveredNodeData && hoveredRect && (
          <NodeCardPreview
            nodeData={hoveredNodeData}
            anchorRect={hoveredRect}
            allNodes={allNodes}
            onClose={peeked ? clearHover : undefined}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default MySolutionsPopup;
